import { forwardRef, type InputHTMLAttributes } from 'react';
import { Check } from 'lucide-react';

export interface CheckboxProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
  label?: string;
  error?: string;
  hint?: string;
}

export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(
  ({ label, error, hint, className = '', id, ...props }, ref) => {
    const checkboxId = id || `checkbox-${Math.random().toString(36).substr(2, 9)}`;

    return (
      <div className={`${className}`}>
        <label
          htmlFor={checkboxId}
          className="flex items-start gap-3 cursor-pointer"
        >
          <div className="relative mt-0.5">
            <input
              ref={ref}
              id={checkboxId}
              type="checkbox"
              className="peer sr-only"
              {...props}
            />
            <div
              className={`
                w-5 h-5 border-2 rounded transition-all duration-200
                peer-focus:ring-2 peer-focus:ring-copper/20
                peer-checked:bg-copper peer-checked:border-copper
                peer-disabled:opacity-50 peer-disabled:cursor-not-allowed
                ${error ? 'border-red-500' : 'border-neutral-300'}
              `}
            >
              <Check
                className="w-full h-full text-white opacity-0 peer-checked:opacity-100 transition-opacity"
                strokeWidth={3}
              />
            </div>
            <Check
              className="absolute inset-0 p-0.5 text-white opacity-0 peer-checked:opacity-100 transition-opacity"
              strokeWidth={3}
            />
          </div>
          {label && (
            <span className="text-sm text-neutral-700">
              {label}
              {props.required && <span className="text-red-500 ml-1">*</span>}
            </span>
          )}
        </label>
        {error && <p className="mt-1 text-sm text-red-500 ml-8">{error}</p>}
        {hint && !error && <p className="mt-1 text-sm text-neutral-500 ml-8">{hint}</p>}
      </div>
    );
  }
);

Checkbox.displayName = 'Checkbox';

// Checkbox Group component for multiple selections
export interface CheckboxOption {
  value: string;
  label: string;
  disabled?: boolean;
}

export interface CheckboxGroupProps {
  label?: string;
  options: CheckboxOption[];
  value: string[];
  onChange: (value: string[]) => void;
  error?: string;
  hint?: string;
  required?: boolean;
  disabled?: boolean;
  className?: string;
}

export function CheckboxGroup({
  label,
  options,
  value,
  onChange,
  error,
  hint,
  required,
  disabled,
  className = '',
}: CheckboxGroupProps) {
  const handleChange = (optionValue: string, checked: boolean) => {
    if (checked) {
      onChange([...value, optionValue]);
    } else {
      onChange(value.filter((v) => v !== optionValue));
    }
  };

  return (
    <div className={className}>
      {label && (
        <label className="block text-sm font-medium text-neutral-700 mb-2">
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}
      <div className="space-y-2">
        {options.map((option) => (
          <Checkbox
            key={option.value}
            label={option.label}
            checked={value.includes(option.value)}
            onChange={(e) => handleChange(option.value, e.target.checked)}
            disabled={disabled || option.disabled}
          />
        ))}
      </div>
      {error && <p className="mt-2 text-sm text-red-500">{error}</p>}
      {hint && !error && <p className="mt-2 text-sm text-neutral-500">{hint}</p>}
    </div>
  );
}
